/* eslint-disable max-lines-per-function */
import React, { useState, useEffect } from "react";
import {
  Typography,
  TextField,
  Button,
  Grid,
  InputAdornment,
  Alert,
  Box,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import ModalBase from "./ModalBase";
import GenericForm from "../GenericForm";

interface PackagingRulesModalProps {
  open: boolean;
  handleClose: () => void;
  rules: {
    max_units_per_parcel: number;
    max_weight_per_parcel: number;
    max_volume_per_parcel: number;
  };
  onSave: (_rules: {
    max_units_per_parcel: number;
    max_weight_per_parcel: number;
    max_volume_per_parcel: number;
  }) => void;
  formErrors: {
    maxUnits?: string;
  };
}

const PackagingRulesModal: React.FC<PackagingRulesModalProps> = ({
  open,
  handleClose,
  rules,
  onSave,
  formErrors,
}) => {
  const [formData, setFormData] = useState({
    maxUnitsPerParcel: rules.max_units_per_parcel || 0,
    maxWeightPerParcel: rules.max_weight_per_parcel || 0,
    maxVolumePerParcel: rules.max_volume_per_parcel || 0,
  });

  useEffect(() => {
    setFormData({
      maxUnitsPerParcel: rules.max_units_per_parcel || 0,
      maxWeightPerParcel: rules.max_weight_per_parcel || 0,
      maxVolumePerParcel: rules.max_volume_per_parcel || 0,
    });
  }, [rules]);

  const handleInputChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: Number(event.target.value) || 0,
      }));
    };

  const handleSubmit = () => {
    onSave({
      max_units_per_parcel: formData.maxUnitsPerParcel,
      max_weight_per_parcel: formData.maxWeightPerParcel,
      max_volume_per_parcel: formData.maxVolumePerParcel,
    });
    handleClose();
  };

  const handleModalClose = () => {
    setFormData({
      maxUnitsPerParcel: rules.max_units_per_parcel || 0,
      maxWeightPerParcel: rules.max_weight_per_parcel || 0,
      maxVolumePerParcel: rules.max_volume_per_parcel || 0,
    });
    handleClose();
  };

  return (
    <ModalBase open={open} handleClose={handleModalClose} maxWidth="sm">
      <>
        {/* Modal Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: "grey.100",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SettingsIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              Límites de empaquetado
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Configurá las reglas globales para todos los pedidos
            </Typography>
          </Box>
        </Box>

        <GenericForm onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Info Alert */}
            <Grid item xs={12}>
              <Alert
                severity="info"
                icon={<InfoIcon fontSize="small" />}
                sx={{ fontSize: "0.8rem", py: 0.5 }}
              >
                Estos límites complementan las dimensiones de cada caja. El
                sistema los usa para optimizar costos logísticos y evitar bultos
                fuera de norma.
              </Alert>
            </Grid>

            {/* Max Units Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Máximo de productos por caja"
                type="number"
                value={formData.maxUnitsPerParcel}
                onChange={handleInputChange("maxUnitsPerParcel")}
                error={!!formErrors.maxUnits}
                helperText={
                  formErrors.maxUnits ||
                  "Límite de unidades distintas que se pueden agrupar en una misma caja. Afecta cómo el sistema divide pedidos grandes."
                }
                inputProps={{ min: 0 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">uds.</InputAdornment>
                  ),
                }}
                size="small"
                sx={{
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.72rem",
                    mt: 0.5,
                  },
                }}
              />
            </Grid>

            {/* Max Weight Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Peso máximo por caja"
                type="number"
                value={formData.maxWeightPerParcel}
                onChange={handleInputChange("maxWeightPerParcel")}
                helperText="Límite superior de peso que actúa como guardia para todas las cajas. Incluí el peso del material de embalaje (papel, cinta, relleno)."
                inputProps={{ min: 0 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">kg</InputAdornment>
                  ),
                }}
                size="small"
                sx={{
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.72rem",
                    mt: 0.5,
                  },
                }}
              />
            </Grid>

            {/* Max Volume Field */}
            {/* <Grid item xs={12}>
              <TextField
                fullWidth
                label="Volumen máximo por caja"
                type="number"
                value={formData.maxVolumePerParcel}
                onChange={handleInputChange("maxVolumePerParcel")}
                helperText="Volumen útil máximo por caja. Usá este límite para evitar sobrepasar la capacidad cúbica de tus cajas más grandes."
                inputProps={{ min: 0 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">cm³</InputAdornment>
                  ),
                }}
                size="small"
                sx={{
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.72rem",
                    mt: 0.5,
                  },
                }}
              />
            </Grid> */}

            {/* Action Buttons */}
            <Grid item xs={12}>
              <div className="d-flex justify-content-end gap-2">
                <Button
                  variant="outlined"
                  onClick={handleModalClose}
                  className="m-r-10"
                >
                  Cancelar
                </Button>
                <Button variant="contained" type="submit" color="primary">
                  Guardar cambios
                </Button>
              </div>
            </Grid>
          </Grid>
        </GenericForm>
      </>
    </ModalBase>
  );
};

export default PackagingRulesModal;
